import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { Minus, Plus, Split } from 'lucide-react'
import { apiClient } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { computeCartTotals, type OrderPricingExtras } from '@/lib/counterPricing'
import {
  computeSplitShares,
  guestPaymentAmount,
  splitAssignmentComplete,
  splittableItems,
  unassignedUnits,
  type GuestShare,
} from '@/lib/splitBilling'
import type {
  OnlineSubMethod,
  Order,
  OrderSplitStateInput,
  PricingSettings,
} from '@/types'
import { SplitAssignmentRow } from './SplitAssignmentRow'
import { SplitGuestCard } from './SplitGuestCard'

export interface PaySplitGuestArgs {
  guestNo: number
  amount: number
  tender: 'cash' | 'card' | 'online'
  subMethod?: string
  share: GuestShare
}

export interface SplitBillDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The order being paid (payOrder). split_state on it is the source of truth. */
  order: Order
  pricing: PricingSettings
  pricingExtras: OrderPricingExtras
  /** total_amount − Σ completed payments, from the freshest order we have */
  serverRemaining: number
  processing: boolean
  formatCurrency: (n: number) => string
  onlineSubMethods: OnlineSubMethod[]
  /** Propagate the refreshed Order returned by the split endpoints. */
  onOrderUpdated: (order: Order) => void
  /** Pre-lock the order's checkout intent (mixed → cash, card_only → card). */
  onSetIntent: (intent: 'cash' | 'card') => void
  onPayGuest: (args: PaySplitGuestArgs) => void
  onReprintGuest?: (share: GuestShare) => void
}

type SplitStep = 'setup' | 'assign' | 'bills'

const GUEST_QUICK_PICKS = [2, 3, 4, 5, 6]

/**
 * Split-bill flow (F-FE-N19): setup (guest count + mode + tender scope) →
 * assign (items mode: per-unit assignment, persisted as it changes) →
 * bills (per-guest cards, each paying through the existing payment
 * mutation with its exact share). The session lives in orders.split_state,
 * so a reload or terminal switch reconstructs everything from the order.
 */
export function SplitBillDialog({
  open,
  onOpenChange,
  order,
  pricing,
  pricingExtras,
  serverRemaining,
  processing,
  formatCurrency,
  onlineSubMethods,
  onOrderUpdated,
  onSetIntent,
  onPayGuest,
  onReprintGuest,
}: SplitBillDialogProps) {
  const split = order.split_state ?? null

  const [step, setStep] = useState<SplitStep>('setup')
  // setup drafts
  const [guestCount, setGuestCount] = useState(2)
  const [mode, setMode] = useState<'items' | 'even'>('items')
  const [cardOnly, setCardOnly] = useState(false)
  // assign draft — mirrors split.assignments, PUT-synced (debounced)
  const [assignments, setAssignments] = useState<Record<string, Record<string, number>>>({})
  const [saving, setSaving] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // (Re)enter the right step whenever the dialog opens: resume an existing
  // session at assignment or bills; otherwise start fresh at setup.
  useEffect(() => {
    if (!open) return
    if (split) {
      setGuestCount(split.guest_count)
      setMode(split.mode)
      setCardOnly(split.tender_scope === 'card_only')
      setAssignments(split.assignments ?? {})
      setStep(
        split.mode === 'items' && !splitAssignmentComplete(order, split) ? 'assign' : 'bills'
      )
    } else {
      setGuestCount(2)
      setMode('items')
      setCardOnly(false)
      setAssignments({})
      setStep('setup')
    }
    // Only when the dialog opens — mid-session order refetches must not
    // yank the operator back to a different step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
    }
  }, [])

  const items = useMemo(() => splittableItems(order), [order])

  // Strip voided/stale item ids before any PUT — the server rejects them.
  const cleanAssignments = useCallback(
    (a: Record<string, Record<string, number>>, guests: number) => {
      const liveIds = new Set(items.map((i) => i.id))
      const out: Record<string, Record<string, number>> = {}
      for (const [itemId, perGuest] of Object.entries(a)) {
        if (!liveIds.has(itemId)) continue
        const cleaned: Record<string, number> = {}
        for (const [g, u] of Object.entries(perGuest)) {
          const gn = parseInt(g, 10)
          if (gn >= 1 && gn <= guests && u > 0) cleaned[g] = u
        }
        if (Object.keys(cleaned).length > 0) out[itemId] = cleaned
      }
      return out
    },
    [items]
  )

  const persistSplit = useCallback(
    async (input: OrderSplitStateInput): Promise<boolean> => {
      setSaving(true)
      try {
        const res = await apiClient.setOrderSplitState(order.id, input)
        if (!res.success || !res.data) {
          toastHelpers.error('Split bill', res.message || 'Could not save the split')
          return false
        }
        onOrderUpdated(res.data)
        return true
      } catch (err) {
        toastHelpers.error(
          'Split bill',
          err instanceof Error ? err.message : 'Could not save the split'
        )
        return false
      } finally {
        setSaving(false)
      }
    },
    [order.id, onOrderUpdated]
  )

  const startSplit = useCallback(async () => {
    const input: OrderSplitStateInput = {
      mode,
      guest_count: guestCount,
      tender_scope: cardOnly ? 'card_only' : 'mixed',
      ...(mode === 'items' ? { assignments: cleanAssignments(assignments, guestCount) } : {}),
    }
    const ok = await persistSplit(input)
    if (!ok) return
    // Lock the order's billing rate before any tender: mixed splits bill at
    // the standard cash rate; card-only keeps the reduced card rate.
    onSetIntent(cardOnly ? 'card' : 'cash')
    setStep(mode === 'items' ? 'assign' : 'bills')
  }, [mode, guestCount, cardOnly, assignments, cleanAssignments, persistSplit, onSetIntent])

  /** Debounced PUT so every stepper tap doesn't fire a request. */
  const queuePersistAssignments = useCallback(
    (next: Record<string, Record<string, number>>) => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        void persistSplit({
          mode: 'items',
          guest_count: split?.guest_count ?? guestCount,
          tender_scope: split?.tender_scope ?? (cardOnly ? 'card_only' : 'mixed'),
          assignments: cleanAssignments(next, split?.guest_count ?? guestCount),
        })
      }, 600)
    },
    [persistSplit, cleanAssignments, split, guestCount, cardOnly]
  )

  const changeAssignment = useCallback(
    (itemId: string, guestIdx: number, delta: number) => {
      setAssignments((prev) => {
        const item = items.find((i) => i.id === itemId)
        if (!item) return prev
        const perGuest = { ...(prev[itemId] ?? {}) }
        const key = String(guestIdx + 1)
        const currentTotal = Object.values(perGuest).reduce((a, b) => a + b, 0)
        const current = perGuest[key] ?? 0
        // Single-unit "radio" behaviour: assigning moves the unit.
        if (delta > 0 && item.quantity === 1) {
          const next = { ...prev, [itemId]: { [key]: 1 } }
          queuePersistAssignments(next)
          return next
        }
        if (delta > 0 && currentTotal >= item.quantity) return prev
        const nextUnits = Math.max(0, current + delta)
        if (nextUnits === 0) delete perGuest[key]
        else perGuest[key] = nextUnits
        const next = { ...prev, [itemId]: perGuest }
        queuePersistAssignments(next)
        return next
      })
    },
    [items, queuePersistAssignments]
  )

  const cancelSplit = useCallback(async () => {
    try {
      const res = await apiClient.clearOrderSplitState(order.id)
      if (!res.success || !res.data) {
        toastHelpers.error('Split bill', res.message || 'Could not cancel the split')
        return
      }
      onOrderUpdated(res.data)
      onOpenChange(false)
    } catch (err) {
      toastHelpers.error(
        'Split bill',
        err instanceof Error ? err.message : 'Could not cancel the split'
      )
    }
  }, [order.id, onOrderUpdated, onOpenChange])

  // ----- derived (bills step) -----

  const scopeIntent: 'cash' | 'card' = split?.tender_scope === 'card_only' ? 'card' : 'cash'
  const totals = useMemo(
    () =>
      computeCartTotals(
        order.subtotal,
        order.discount_amount,
        scopeIntent,
        pricing,
        pricingExtras
      ),
    [order.subtotal, order.discount_amount, scopeIntent, pricing, pricingExtras]
  )

  const shares: GuestShare[] = useMemo(() => {
    if (!split) return []
    return computeSplitShares(order, totals, split)
  }, [order, totals, split])

  const paidCount = shares.filter((s) => s.paid).length
  const anyPaid = paidCount > 0
  const missingUnits = split ? unassignedUnits(order, { ...split, assignments }) : 0

  const stepForRender: SplitStep = split ? step : 'setup'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden sm:rounded-lg">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-4 w-4" aria-hidden />
            Split bill
            {split && (
              <span className="text-sm font-normal text-muted-foreground">
                · {split.guest_count} guests · {split.mode === 'items' ? 'by items' : 'evenly'}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {stepForRender === 'setup' &&
              'Divide this check across guests. The order stays one bill with one tax invoice.'}
            {stepForRender === 'assign' && 'Assign every item to a guest, then continue to payment.'}
            {stepForRender === 'bills' &&
              `${paidCount} of ${split?.guest_count ?? 0} guests paid — ${formatCurrency(Math.max(0, serverRemaining))} remaining.`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {stepForRender === 'setup' && (
            <>
              <div>
                <p className="mb-1.5 text-sm font-medium">How many guests?</p>
                <div className="flex items-center gap-1.5">
                  {GUEST_QUICK_PICKS.map((n) => (
                    <Button
                      key={n}
                      type="button"
                      variant={guestCount === n ? 'default' : 'outline'}
                      size="sm"
                      className="h-10 w-10 text-sm font-bold tabular-nums"
                      onClick={() => setGuestCount(n)}
                    >
                      {n}
                    </Button>
                  ))}
                  <div className="ml-1 flex items-center gap-1 rounded-md border border-border px-1 py-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      disabled={guestCount <= 2}
                      onClick={() => setGuestCount((v) => Math.max(2, v - 1))}
                      aria-label="Fewer guests"
                    >
                      <Minus className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                    <span className="w-6 text-center text-sm font-bold tabular-nums">{guestCount}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      disabled={guestCount >= 30}
                      onClick={() => setGuestCount((v) => Math.min(30, v + 1))}
                      aria-label="More guests"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-sm font-medium">How should the bill divide?</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button
                    type="button"
                    variant={mode === 'items' ? 'default' : 'outline'}
                    className="h-auto flex-col items-start gap-0.5 px-3 py-2 text-left"
                    onClick={() => setMode('items')}
                  >
                    <span className="text-sm font-semibold">By items</span>
                    <span
                      className={cn(
                        'text-xs font-normal',
                        mode === 'items' ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      )}
                    >
                      Each guest pays for what they had
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant={mode === 'even' ? 'default' : 'outline'}
                    className="h-auto flex-col items-start gap-0.5 px-3 py-2 text-left"
                    onClick={() => setMode('even')}
                  >
                    <span className="text-sm font-semibold">Evenly</span>
                    <span
                      className={cn(
                        'text-xs font-normal',
                        mode === 'even' ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      )}
                    >
                      Total ÷ {guestCount} guests
                    </span>
                  </Button>
                </div>
              </div>

              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border p-2.5">
                <Checkbox
                  checked={cardOnly}
                  onCheckedChange={(v) => setCardOnly(v === true)}
                  className="mt-0.5"
                />
                <span className="text-xs leading-snug">
                  <span className="font-medium">Card-only split</span>
                  <span className="block text-muted-foreground">
                    Keeps the reduced card tax rate — cash payments will be rejected. Leave off if
                    anyone might pay cash (the bill is then taxed at the standard cash rate).
                  </span>
                </span>
              </label>
            </>
          )}

          {stepForRender === 'assign' && split && (
            <>
              {missingUnits > 0 && (
                <p className="rounded-lg border border-amber-400/60 bg-amber-50/60 px-2.5 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                  {missingUnits} unit{missingUnits === 1 ? '' : 's'} still unassigned
                </p>
              )}
              {items.map((it) => {
                const perGuest = assignments[it.id] ?? {}
                const units = Array.from(
                  { length: split.guest_count },
                  (_, g) => perGuest[String(g + 1)] ?? 0
                )
                return (
                  <SplitAssignmentRow
                    key={it.id}
                    item={it}
                    guestCount={split.guest_count}
                    units={units}
                    onChange={(guestIdx, delta) => changeAssignment(it.id, guestIdx, delta)}
                    formatCurrency={formatCurrency}
                  />
                )
              })}
            </>
          )}

          {stepForRender === 'bills' && split && (
            <>
              {shares.map((share) => (
                <SplitGuestCard
                  key={share.guestNo}
                  share={share}
                  payableAmount={guestPaymentAmount(share, shares, serverRemaining)}
                  mode={split.mode}
                  guestCount={split.guest_count}
                  cardOnly={split.tender_scope === 'card_only'}
                  onlineSubMethods={onlineSubMethods}
                  processing={processing}
                  payLocked={processing}
                  formatCurrency={formatCurrency}
                  onPay={(tender, subMethod) =>
                    onPayGuest({
                      guestNo: share.guestNo,
                      amount: guestPaymentAmount(share, shares, serverRemaining),
                      tender,
                      subMethod,
                      share,
                    })
                  }
                  onReprint={
                    share.paid && onReprintGuest ? () => onReprintGuest(share) : undefined
                  }
                />
              ))}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:justify-between">
          {stepForRender === 'setup' && (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button type="button" disabled={saving} onClick={startSplit}>
                {saving ? 'Starting…' : mode === 'items' ? 'Assign items' : 'Show bills'}
              </Button>
            </>
          )}
          {stepForRender === 'assign' && (
            <>
              <Button type="button" variant="ghost" disabled={anyPaid} onClick={cancelSplit}>
                Cancel split
              </Button>
              <Button
                type="button"
                disabled={missingUnits > 0 || saving}
                onClick={async () => {
                  // Flush any pending debounce with the final assignment map.
                  if (saveTimer.current) clearTimeout(saveTimer.current)
                  const ok = await persistSplit({
                    mode: 'items',
                    guest_count: split?.guest_count ?? guestCount,
                    tender_scope: split?.tender_scope ?? (cardOnly ? 'card_only' : 'mixed'),
                    assignments: cleanAssignments(assignments, split?.guest_count ?? guestCount),
                  })
                  if (ok) setStep('bills')
                }}
              >
                {saving ? 'Saving…' : 'Continue to bills'}
              </Button>
            </>
          )}
          {stepForRender === 'bills' && (
            <>
              {anyPaid ? (
                <span className="self-center text-xs text-muted-foreground">
                  Split locked — {paidCount} guest{paidCount === 1 ? '' : 's'} settled
                </span>
              ) : (
                <Button type="button" variant="ghost" onClick={cancelSplit}>
                  Cancel split
                </Button>
              )}
              <div className="flex gap-2">
                {split?.mode === 'items' && !anyPaid && (
                  <Button type="button" variant="outline" onClick={() => setStep('assign')}>
                    Edit items
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Hide
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
